'use client';

// The only boundary that catches a throw in the ROOT LAYOUT. When the root
// layout is the thing that failed, Next replaces the whole document with this
// file, so it must render its own <html> and <body>.
//
// That is why every style here is inline and every value is literal. The token
// sheet (globals.css / vireo.css) is loaded by the layout that just died, so
// `var(--ink)` would resolve to nothing and this page would render as unstyled
// black-on-white text. The colours below are the brand navy and amber written
// out longhand, on purpose. Do not "tidy" them into tokens.
//
// It also cannot use next/link (no router guarantee at this point) or the brand
// components (they import CSS that is not loaded). A plain <a> and a text
// wordmark are the whole vocabulary.

import { useErrorReport, type BoundaryError } from './_lib/diag/use-error-report';

export default function GlobalError({ error, reset }: { error: BoundaryError; reset: () => void }) {
  // Still reports. DiagBoot lives in the layout that failed, so initDiagCapture
  // may never have run and the breadcrumb trail may be empty — but captureError
  // does not depend on init, so the event, its stack and its fingerprint still
  // reach error_events. An empty crumb list on a global-error row is a signal in
  // itself: it means the layout itself threw.
  useErrorReport(error, 'global');

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          padding: '32px 24px',
          background: '#1a2238',
          color: '#f4f1ea',
          font: '400 16px/1.55 ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
          WebkitFontSmoothing: 'antialiased',
        }}
      >
        <main style={{ maxWidth: 520, textAlign: 'center' }}>
          <div
            style={{
              fontSize: 13,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              opacity: 0.62,
              marginBottom: 22,
            }}
          >
            Vireo
          </div>

          <h1
            style={{
              margin: '0 0 14px',
              // No serif token available here, so the stack is spelled out.
              font: '400 clamp(26px, 6vw, 38px)/1.18 ui-serif, Georgia, "Times New Roman", serif',
              color: '#f4f1ea',
            }}
          >
            The page could not load.
          </h1>

          <p style={{ margin: '0 0 28px', opacity: 0.78, fontSize: 15.5 }}>
            Something failed before the page could start. It has been reported. Reloading usually
            fixes it.
          </p>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={reset}
              style={{
                appearance: 'none',
                border: 0,
                cursor: 'pointer',
                borderRadius: 999,
                padding: '13px 26px',
                fontSize: 15,
                fontWeight: 600,
                background: '#e8b84b',
                color: '#1a2238',
              }}
            >
              Reload
            </button>
            <a
              href="/"
              style={{
                borderRadius: 999,
                padding: '13px 26px',
                fontSize: 15,
                fontWeight: 600,
                textDecoration: 'none',
                color: '#f4f1ea',
                border: '1px solid rgba(244,241,234,0.28)',
              }}
            >
              Back to the map
            </a>
          </div>
        </main>
      </body>
    </html>
  );
}
